import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "comet-trails";

const DEFAULT_BG = "#050816";
const DEFAULT_HEAD = "#ffffff";
const DEFAULT_TAIL = "#a78bfa";

const SPAWN_INTERVAL_MS = 1700;   // base time between new comets
const SPAWN_JITTER_MS = 1700;     // random extra delay
const MAX_COMETS = 6;             // concurrent comets — keep low for drama

function rand(min, max) { return min + Math.random() * (max - min); }

export default function CometTrails({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_HEAD, DEFAULT_TAIL]);
  const stateRef = useRef({
    comets: [],
    lastSpawnAt: 0,
    nextSpawnDelay: 0,
    twinkles: [], // dim background stars
  });

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_HEAD),
      pickColor(colors, 2, DEFAULT_TAIL),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    // A comet is described by start point + direction + speed + length.
    // We track its head position over time and render a long tail behind
    // it as a line whose alpha falls off toward the start of the streak.
    const spawnComet = () => {
      // Start somewhere off-screen (left or top) and travel toward the
      // opposite side. Angle is biased to the canonical 'shooting star'
      // diagonal so it always reads as a comet rather than horizontal.
      const fromTop = Math.random() < 0.5;
      const angle = fromTop
        ? rand(Math.PI * 0.18, Math.PI * 0.40)   // top-left → bottom-right-ish
        : rand(Math.PI * 0.10, Math.PI * 0.32);  // left → upper-right-ish
      const speed = rand(2.4, 4.6);              // px/frame
      const tailLen = rand(180, 360);
      let startX;
      let startY;
      if (fromTop) {
        startX = rand(-50, width * 0.8);
        startY = -50;
      } else {
        startX = -50;
        startY = rand(0, height * 0.8);
      }
      stateRef.current.comets.push({
        x: startX,
        y: startY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        tailLen,
        size: rand(1.4, 2.6),
        born: performance.now(),
      });
    };

    const seedTwinkles = () => {
      const count = Math.floor((width * height) / 9000);
      const arr = new Array(count);
      for (let i = 0; i < count; i += 1) {
        arr[i] = {
          x: rand(0, width),
          y: rand(0, height),
          r: rand(0.3, 1.0),
          phase: rand(0, Math.PI * 2),
          period: rand(0.5, 1.6),
        };
      }
      stateRef.current.twinkles = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedTwinkles();
    };

    const tick = (ts) => {
      const [bg, head, tail] = colorsRef.current;
      const state = stateRef.current;

      // Deep-space backdrop with a subtle vignette so the comet streaks
      // feel like they cross a real night sky rather than a flat fill.
      const grad = ctx.createRadialGradient(
        width / 2, height / 2, 0,
        width / 2, height / 2, Math.max(width, height)
      );
      grad.addColorStop(0, bg);
      grad.addColorStop(1, rgba(bg, 0.92));
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      // Twinkling background stars — soft, never the focus.
      const t = ts / 1000;
      for (let i = 0; i < state.twinkles.length; i += 1) {
        const s = state.twinkles[i];
        const a = 0.18 + 0.32 * (0.5 + 0.5 * Math.sin(t * s.period + s.phase));
        ctx.fillStyle = rgba(head, a);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Spawn a new comet on cadence.
      if (state.lastSpawnAt === 0) {
        state.lastSpawnAt = ts;
        state.nextSpawnDelay = SPAWN_INTERVAL_MS + Math.random() * SPAWN_JITTER_MS;
      }
      if (ts - state.lastSpawnAt > state.nextSpawnDelay && state.comets.length < MAX_COMETS) {
        spawnComet();
        state.lastSpawnAt = ts;
        state.nextSpawnDelay = SPAWN_INTERVAL_MS + Math.random() * SPAWN_JITTER_MS;
      }

      // Update + render comets.
      ctx.globalCompositeOperation = "lighter";
      const comets = state.comets;
      for (let i = comets.length - 1; i >= 0; i -= 1) {
        const c = comets[i];
        c.x += c.vx;
        c.y += c.vy;

        // Cull once the comet has fully exited the canvas.
        if (c.x - c.tailLen > width + 50 || c.y - c.tailLen > height + 50) {
          comets.splice(i, 1);
          continue;
        }

        // Tail vector (pointing back from head along the inverse velocity).
        const dirLen = Math.sqrt(c.vx * c.vx + c.vy * c.vy);
        const ux = -c.vx / dirLen;
        const uy = -c.vy / dirLen;
        const tailEndX = c.x + ux * c.tailLen;
        const tailEndY = c.y + uy * c.tailLen;

        // Linear gradient from head (bright) to tail end (transparent).
        const lg = ctx.createLinearGradient(c.x, c.y, tailEndX, tailEndY);
        lg.addColorStop(0, rgba(head, 0.95));
        lg.addColorStop(0.15, rgba(tail, 0.7));
        lg.addColorStop(1, rgba(tail, 0));
        ctx.strokeStyle = lg;
        ctx.lineCap = "round";
        ctx.lineWidth = c.size;
        ctx.beginPath();
        ctx.moveTo(c.x, c.y);
        ctx.lineTo(tailEndX, tailEndY);
        ctx.stroke();

        // Head halo — bright glow at the comet's leading point so the eye
        // tracks it naturally.
        const haloR = c.size * 14;
        const halo = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, haloR);
        halo.addColorStop(0, rgba(head, 0.7));
        halo.addColorStop(0.4, rgba(tail, 0.25));
        halo.addColorStop(1, rgba(tail, 0));
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(c.x, c.y, haloR, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = "source-over";

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
